import styled from '@emotion/styled';
import ReviewImageGallery from './ReviewImageGallery';
import type { Review, ReviewImage } from '..';
import { StarIcon } from '../../../assets/icons/StarIcon';

interface ReviewHeaderProps {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    [key: number]: number;
  };
  onlyScore?: boolean;
  onlyDistribution?: boolean;
  customerImages?: ReviewImage[];
  reviews?: Review[];
  onViewAllImages?: () => void;
}

const Container = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
    flex-wrap: nowrap;
    height: 131px;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 32px;
  }
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;

  @media (min-width: 768px) {
    gap: 24px;
    flex: 1;
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 24px 0;
  gap: 8px;
  border-bottom: 1px solid #e9eaeb;
`;

const EmptyStateSubtitle = styled.div`
  color: var(--Text-in-light-bg-100, #2e2e2e);

  /* Body 3/Bold */
  font-family: 'Plus Jakarta Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 140%; /* 19.6px */
`;

const EmptyStateText = styled.div`
  color: var(--Text-in-light-bg-100, #2e2e2e);
  font-feature-settings: 'ss10' on;

  /* Caption 1/Regular */
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 16.8px */
`;

const RatingSummary = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 133px;
  height: fit-content;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    min-width: 220px;
    max-width: 260px;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const RatingCircle = styled.div`
  width: 70px;
  height: 70px;
  background: #3d1690;
  border-radius: 14px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans';
  font-size: 30px;
  font-style: normal;
  font-weight: 700;

  @media (min-width: 768px) {
    width: 70px;
    height: 70px;
    padding: 10px;
    border-radius: 14px;
    font-size: 30px;
    line-height: 50px;
    margin-right: 16px;
    margin-bottom: 0;
  }
`;

const StarBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;

  @media (min-width: 768px) {
    flex-direction: column-reverse;
    align-items: flex-start;
    gap: 16px;
  }
`;

const RatingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  @media (min-width: 768px) {
    align-items: flex-start;
    gap: 11px;
  }
`;

const ReviewCount = styled.p`
  font-size: 12px;
  color: #6e6e6e;
  margin: 4px 0 0 0;
  text-align: center;
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  @media (min-width: 768px) {
    font-size: 14px;
    text-align: left;
  }
`;

const StarContainer = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledStarIcon = styled(StarIcon)`
  width: 26px;
  height: 26px;
  aspect-ratio: 1/1;

  @media (min-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const Star = styled.span`
  color: #ffd700;
  font-size: 16px;
  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

const RatingText = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #2e2e2e;
  margin: 0;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 18px;
    font-weight: 500;
    margin-top: 0;
    margin-bottom: 0;
    text-align: left;
  }
`;

const Divider = styled.div`
  width: 1px;
  min-width: 1px;
  background: #dcdcdc;
  height: 141px;
  flex-shrink: 0;

  @media (min-width: 768px) {
    height: 131px;
  }
`;

const ResponsiveDivider = styled.div`
  background: #dcdcdc;
  width: 100%;
  height: 1px;
  margin: 0;

  @media (min-width: 768px) {
    width: 1px;
    height: 131px;
    flex-shrink: 0;
  }
`;

const RatingDistribution = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 141px;
  min-width: 200px;

  @media (min-width: 768px) {
    height: 131px;
  }
`;

const RatingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RatingNumber = styled.span`
  font-size: 14px;
  color: #2e2e2e;
  min-width: 10px;
`;

const ProgressBar = styled.div<{ percentage: number }>`
  width: 75px;
  height: 5px;
  background: #dcdcdc;
  border-radius: 4px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${(props) => props.percentage}%;
    background: #303030;
    border-radius: 4px;
  }

  @media (min-width: 768px) {
    width: 127px;
  }
`;

const RatingLabel = styled.span`
  font-family: 'Plus Jakarta Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const ratingLabels = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Excellent',
  5: 'Outstanding',
} as const;

export const ReviewHeader = ({
  averageRating,
  totalReviews,
  ratingDistribution,
  onlyScore = false,
  onlyDistribution = false,
  customerImages = [],
  reviews = [],
  onViewAllImages,
}: ReviewHeaderProps) => {
  if (totalReviews === 0) {
    return (
      <EmptyStateContainer>
        <EmptyStateSubtitle>Be the first to write a review!</EmptyStateSubtitle>
        <EmptyStateText>Tell others what you think!</EmptyStateText>
      </EmptyStateContainer>
    );
  }

  if (onlyScore) {
    return (
      <Container>
        <RatingSummary>
          <RatingCircle>{averageRating.toFixed(1)}</RatingCircle>
          <StarBlock>
            <RatingWrapper>
              <StarContainer>
                {[1, 2, 3, 4, 5].map((star) => (
                  <StyledStarIcon key={star} filled={star <= Math.round(averageRating)} />
                ))}
              </StarContainer>
              <RatingWrapper>
                <RatingText>{ratingLabels[Math.ceil(averageRating)]}</RatingText>
                <ReviewCount>
                  Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
                </ReviewCount>
              </RatingWrapper>
            </RatingWrapper>
          </StarBlock>
        </RatingSummary>
      </Container>
    );
  }

  const getDistributionPercentage = (rating: number): number => {
    const count = ratingDistribution[rating] || 0;
    return (count / totalReviews) * 100;
  };

  return (
    <Container>
      <TopSection>
        <RatingSummary>
          <RatingCircle>{averageRating.toFixed(1)}</RatingCircle>
          <StarBlock>
            <StarContainer>
              {[1, 2, 3, 4, 5].map((star) => (
                <StyledStarIcon key={star} filled={star <= Math.round(averageRating)} />
              ))}
            </StarContainer>
            <RatingWrapper>
              <RatingText>{ratingLabels[Math.ceil(averageRating)]}</RatingText>
              <ReviewCount>
                Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
              </ReviewCount>
            </RatingWrapper>
          </StarBlock>
        </RatingSummary>

        {!onlyDistribution && <Divider />}

        {!onlyDistribution && (
          <RatingDistribution>
            {Object.entries(ratingDistribution)
              .sort(([a], [b]) => Number(b) - Number(a))
              .map(([rating]) => (
                <RatingBar key={rating}>
                  <RatingNumber>{rating}</RatingNumber>
                  <ProgressBar percentage={getDistributionPercentage(Number(rating))} />
                  <RatingLabel>{ratingLabels[rating as unknown as keyof typeof ratingLabels]}</RatingLabel>
                </RatingBar>
              ))}
          </RatingDistribution>
        )}
      </TopSection>
      {!onlyDistribution && <ResponsiveDivider />}
      {reviews && reviews.length > 0 && (
        <div style={{ flex: 1, minWidth: 0 }}>
          <ReviewImageGallery images={customerImages} onViewAll={onViewAllImages} />
        </div>
      )}
    </Container>
  );
};

export default ReviewHeader;
